import React, { useCallback, useEffect, useMemo } from 'react';
import { Loader } from 'src/@vymo/ui/atoms';
import { NoData } from 'src/@vymo/ui/blocks';
import Table from 'src/@vymo/ui/blocks/table/index';
import { Filters } from 'src/@vymo/ui/molecules';
import { FormVersion } from 'src/@vymo/ui/molecules/form/types';
import { useFilterPersistence } from 'src/hooks/useFilterPersistence';
import { getWorkflowActionState } from 'src/models/lmsConfig/selectors';
import { resetWorkflowAction } from 'src/models/lmsConfig/slice';
import {
  clearModuleButtons,
  setModuleButtons,
} from 'src/models/moduleButtons/slice';
import { WORKFLOW_FORM } from 'src/modules/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { getWorkspaceDialogs } from 'src/workspace/selectors';
import { renderDialog } from 'src/workspace/slice';
import { getGenericListDisplayName, handleRowClick } from './queries';
import {
  getGenericListFilterFields,
  getGenericListFilters,
  getGenericListLoading,
  getGenericListMetaData,
  getGenericListState,
} from './selector';
import {
  resetFilters,
  setCurrentPageNumber,
  setFilters,
  setGenericListPageSize,
} from './slice';
import { fetchModuleTableData } from './thunk';
import styles from './index.module.scss';

function GenericList(props) {
  const { module } = props;
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(getGenericListLoading);
  const { columns, rows: listData } = useAppSelector(getGenericListState);
  const { pageSize, currentPage, maxPages, totalCount } = useAppSelector(
    getGenericListMetaData,
  );
  const filterFields = useAppSelector(getGenericListFilterFields);
  const filters = useAppSelector(getGenericListFilters);
  const dialogs = useAppSelector(getWorkspaceDialogs);
  const workflowFormDialog = dialogs?.[WORKFLOW_FORM];
  const workflowActionState = useAppSelector(getWorkflowActionState);

  const filterCount = useMemo(
    () =>
      Object.keys(filters || {}).filter(
        (key) => filters[key] != null && filters[key] !== '',
      ).length,
    [filters],
  );

  const handleFiltersChange = useCallback(
    (persistedFilters: Record<string, any>) => {
      dispatch(setFilters(persistedFilters));
    },
    [dispatch],
  );

  useFilterPersistence(
    `genericList_${module}`,
    filters || {},
    handleFiltersChange,
  );

  const handleApply = useMemo(
    () => (appliedFilters: Record<string, any>) => {
      dispatch(setFilters(appliedFilters));
      dispatch(setCurrentPageNumber(1));
      dispatch(fetchModuleTableData({ module }));
    },
    [dispatch, module],
  );

  const handleReset = useMemo(
    () => () => {
      dispatch(resetFilters());
      dispatch(setCurrentPageNumber(1));
      dispatch(fetchModuleTableData({ module }));
    },
    [dispatch, module],
  );

  const getModuleDisplayName = useCallback((moduleCode: string): string => {
    if (!moduleCode) return '';

    return moduleCode
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, []);

  const handleAddClick = useCallback(() => {
    dispatch(
      renderDialog({
        id: WORKFLOW_FORM,
        props: {
          moduleCode: module,
          voId: 'global',
          action: 'add_lead',
        },
      }),
    );
  }, [dispatch, module]);

  // Set module buttons when component mounts (excluding DDP)
  useEffect(() => {
    // Only set buttons if module is defined and not DDP
    if (module && module !== 'workflow-ddp-status') {
      const moduleName = getModuleDisplayName(module);
      if (moduleName) {
        dispatch(
          setModuleButtons([
            {
              label: `Add ${moduleName}`,
              onClick: handleAddClick,
              variant: 'default',
              'data-test-id': `add-${module}-button`,
            },
          ]),
        );
      }
    } else if (module === 'workflow-ddp-status') {
      // Clear buttons if module is DDP
      dispatch(clearModuleButtons());
    }
    return () => {
      dispatch(clearModuleButtons());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, module]);

  // Refresh list when workflow form completes successfully and dialog closes
  useEffect(() => {
    if (workflowActionState === 'success' && !workflowFormDialog && module) {
      dispatch(fetchModuleTableData({ module }));
      dispatch(resetWorkflowAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowActionState, workflowFormDialog]);

  if (!columns.length && !isLoading) {
    return <NoData classNames={styles.genericList__pageBody} />;
  }

  return (
    <div className={styles.genericList}>
      <div className={styles.genericList__actions}>
        {filterFields.length > 0 && (
          <Filters
            filterFields={filterFields}
            metaData={{}}
            formVersion={FormVersion.webRevyre}
            filters={filters}
            filterCount={filterCount}
            onApply={handleApply}
            onReset={handleReset}
          />
        )}
      </div>

      <Loader visible={isLoading} className={styles.genericList__loader}>
        <Table
          data-test-id="download-list"
          isAccordion={false}
          tableData={listData}
          columnConfigs={columns}
          tableConfig={{
            showSearch: false,
            isRowSelectionEnabled: false,
            name: getGenericListDisplayName(module),
            pagination: {
              visible: true,
              pageSize,
              totalPages: maxPages,
              currentPage,
              totalItems: totalCount,
            },
            onRowClick: (rowId, rowData) => {
              handleRowClick({
                dispatch,
                module,
                rowId,
                rowData,
              });
            },
          }}
          onChangePageSize={(updatedPageSize) => {
            dispatch(setGenericListPageSize(updatedPageSize));
            dispatch(fetchModuleTableData({ module }));
          }}
          onChangePageIndex={(pageIndex) => {
            dispatch(setCurrentPageNumber(Number(pageIndex) + 1));
            dispatch(fetchModuleTableData({ module }));
          }}
        />
      </Loader>
    </div>
  );
}

export default GenericList;
